import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, Paperclip, Plus } from "@phosphor-icons/react";
import { parseNum } from "@/lib/utils";

export const fileUrl = (id) => `${API}/files/${id}?auth=${localStorage.getItem("token")}`;
const today = () => new Date().toISOString().slice(0, 10);

function NewCategory({ onCreated }) {
  const [label, setLabel] = useState("");
  const [ded, setDed] = useState(false);
  const save = async () => {
    if (!label.trim()) return;
    try { const { data } = await api.post("/personal/categories", { label, deductible: ded }); setLabel(""); setDed(false); onCreated(data); toast.success("Categoria criada"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  return (
    <div className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid="new-category-box">
      <Label className="text-xs">Nova categoria</Label>
      <div className="flex gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex: Academia" data-testid="new-category-name" />
        <Button type="button" onClick={save} data-testid="btn-save-category" className="bg-blue-600 text-white">Criar</Button>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={ded} onCheckedChange={(v) => setDed(!!v)} data-testid="new-category-deductible" /> Dedutível no Imposto de Renda</label>
    </div>
  );
}

export function PersonalEntryDialog({ entry, cats, onClose, onSaved, onCatsChanged }) {
  const [form, setForm] = useState({});
  const [showNewCat, setShowNewCat] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (entry) setForm({ kind: "expense", category: "", description: "", date: today(), value: "", receipt_file_id: null, ...entry, value: entry.value ? String(entry.value).replace(".", ",") : "" });
    setShowNewCat(false);
  }, [entry]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const list = form.kind === "income" ? cats.income : cats.expense;

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((x) => ({ ...x, receipt_file_id: data.id })); toast.success("Comprovante anexado"); }
    catch { toast.error("Falha no envio do comprovante"); } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Escolha a categoria");
    const payload = { kind: form.kind, category: form.category, description: form.description, date: form.date, value: parseNum(form.value), receipt_file_id: form.receipt_file_id };
    try {
      if (entry?.id) await api.put(`/personal/entries/${entry.id}`, payload); else await api.post("/personal/entries", payload);
      toast.success("Lançamento salvo"); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="personal-entry-dialog">
        <DialogHeader><DialogTitle>{entry?.id ? "Editar lançamento" : form.kind === "income" ? "Nova renda" : "Nova despesa pessoal"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 gap-3">
          <div><Label>Tipo</Label>
            <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v, category: "" }))}>
              <SelectTrigger data-testid="personal-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="expense">Despesa</SelectItem><SelectItem value="income">Renda (outros ganhos)</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Categoria</Label>
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger data-testid="personal-category"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{(list || []).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}{c.deductible ? " · dedutível IR" : ""}</SelectItem>)}</SelectContent>
            </Select>
            {form.kind === "expense" && (
              <button type="button" onClick={() => setShowNewCat((v) => !v)} data-testid="btn-toggle-new-category" className="text-xs text-blue-700 mt-1 inline-flex items-center gap-1"><Plus size={12} /> Criar categoria própria</button>
            )}
          </div>
          {showNewCat && <NewCategory onCreated={(c) => { onCatsChanged(); setForm((f) => ({ ...f, category: c.key })); setShowNewCat(false); }} />}
          <div><Label>Descrição</Label><Input value={form.description || ""} onChange={set("description")} placeholder="Ex: Consulta dentista, mensalidade escola" data-testid="personal-description" /></div>
          <div><Label>Data</Label><Input type="date" value={form.date || ""} onChange={set("date")} data-testid="personal-date" /></div>
          <div><Label>Valor (R$)</Label><Input inputMode="decimal" value={form.value || ""} onChange={set("value")} placeholder="ex: 250,00" data-testid="personal-value" /></div>
          <div><Label>Comprovante (foto ou PDF)</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border cursor-pointer text-sm hover:bg-slate-50" data-testid="personal-receipt-label">
                <Camera size={16} /> {uploading ? "Enviando..." : form.receipt_file_id ? "Trocar comprovante" : "Anexar"}
                <input type="file" accept="image/*,application/pdf" hidden onChange={upload} data-testid="personal-receipt-input" />
              </label>
              {form.receipt_file_id && <a href={fileUrl(form.receipt_file_id)} target="_blank" rel="noreferrer" className="text-xs text-blue-700 inline-flex items-center gap-1" data-testid="personal-receipt-link"><Paperclip size={12} /> Ver anexo</a>}
            </div>
          </div>
          <DialogFooter><Button type="submit" disabled={uploading} data-testid="btn-save-personal" className="bg-blue-600 hover:bg-blue-700 text-white">Salvar</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
